import {
    APP_STYLE_COMPONENT_COLORS as MSI_COMPONENT_COLORS,
    MSI_GEOMETRY,
    APP_STYLE_PALETTE as MSI_PALETTE,
    getAppStyleTokens,
    MSI_TYPE_SCALE,
    usesAppStyleGrammar,
} from './msiStyleTokens';

const EXTERNAL_DARK_CHROME = Object.freeze({
    panel: '#000',
    inset: 'rgba(2, 8, 20, 0.28)',
    control: '#000',
    controlHover: '#131a21',
    controlBorder: '#555',
    controlHoverBorder: '#888',
    text: '#fff',
    secondaryText: '#cbd5e1',
    mutedText: '#9fb3c7',
    grid: '#444',
    axis: '#fff',
    reference: '#888',
    tooltip: '#000',
    tooltipBorder: '#fff',
});

export function getPerformanceChartChrome(theme) {
    if (!usesAppStyleGrammar(theme)) {
        return {
            ...EXTERNAL_DARK_CHROME,
            panel: theme?.palette?.mode === 'dark'
                ? EXTERNAL_DARK_CHROME.panel
                : theme?.palette?.background?.paper,
        };
    }

    return {
        panel: MSI_PALETTE.surface,
        inset: MSI_PALETTE.surfaceSubtle,
        control: MSI_PALETTE.surface,
        controlHover: MSI_COMPONENT_COLORS.secondaryHover,
        controlBorder: MSI_PALETTE.lineDark,
        controlHoverBorder: MSI_COMPONENT_COLORS.secondaryHoverBorder,
        text: MSI_PALETTE.ink900,
        secondaryText: MSI_PALETTE.ink700,
        mutedText: MSI_PALETTE.ink500,
        grid: MSI_COMPONENT_COLORS.rowDivider,
        axis: MSI_PALETTE.lineDark,
        reference: MSI_PALETTE.ink400,
        tooltip: MSI_PALETTE.surface,
        tooltipBorder: MSI_PALETTE.line,
    };
}

// The Compare & Explore stress grid's diverging scale: the theme's positive
// and negative tokens (recolourable in Appearance) in six steps each, grey
// within the first cut. Cuts are fixed fractions of opening gross so colours
// compare across grids and sessions; `gap` is the vs-baseline view. Dark
// themes take stronger tints to read on a near-black surface, capped so the
// theme's ink stays legible on the strongest step.
export const STRESS_HEAT_CUTS = Object.freeze({
    result: Object.freeze([0.01, 0.03, 0.05, 0.08, 0.11, 0.14]),
    gap: Object.freeze([0.005, 0.015, 0.03, 0.05, 0.07, 0.09]),
});
const STRESS_HEAT_ALPHAS = Object.freeze({
    light: Object.freeze([0.1, 0.18, 0.27, 0.36, 0.46, 0.56]),
    dark: Object.freeze([0.14, 0.22, 0.3, 0.38, 0.46, 0.55]),
});

export function stressHeatStep(value, basis = 'result') {
    if (!Number.isFinite(value)) return null;
    const cuts = STRESS_HEAT_CUTS[basis] || STRESS_HEAT_CUTS.result;
    return cuts.filter((cut) => Math.abs(value) >= cut).length;
}

export function stressHeatColor(value, { basis = 'result', dark = false } = {}) {
    const step = stressHeatStep(value, basis);
    if (step === null) return null;
    if (step === 0) return `rgb(var(--c-text-secondary, 189 189 189) / ${dark ? 0.12 : 0.1})`;
    const alpha = STRESS_HEAT_ALPHAS[dark ? 'dark' : 'light'][step - 1];
    return value > 0
        ? `rgb(var(--c-positive, 46 125 50) / ${alpha})`
        : `rgb(var(--c-negative, 198 40 40) / ${alpha})`;
}

export const performancePanelSx = {
    border: (theme) => (
        usesAppStyleGrammar(theme) ? `1px solid ${MSI_PALETTE.line}` : undefined
    ),
    borderRadius: (theme) => (
        usesAppStyleGrammar(theme) ? `${MSI_GEOMETRY.radii.panel}px` : undefined
    ),
    backgroundColor: (theme) => (
        usesAppStyleGrammar(theme) ? MSI_PALETTE.surface : undefined
    ),
    backgroundImage: (theme) => (usesAppStyleGrammar(theme) ? 'none' : undefined),
    boxShadow: (theme) => (usesAppStyleGrammar(theme) ? 'none' : undefined),
};

export const performanceInsetSx = {
    border: (theme) => (
        usesAppStyleGrammar(theme) ? `1px solid ${MSI_PALETTE.line}` : undefined
    ),
    borderRadius: (theme) => (
        usesAppStyleGrammar(theme) ? `${MSI_GEOMETRY.radii.button}px` : undefined
    ),
    backgroundColor: (theme) => (
        usesAppStyleGrammar(theme) ? MSI_PALETTE.surfaceSubtle : undefined
    ),
    backgroundImage: (theme) => (usesAppStyleGrammar(theme) ? 'none' : undefined),
    boxShadow: (theme) => (usesAppStyleGrammar(theme) ? 'none' : undefined),
};

export const performanceToggleGroupSx = {
    border: (theme) => `1px solid ${getPerformanceChartChrome(theme).controlBorder}`,
    borderRadius: (theme) => (
        usesAppStyleGrammar(theme) ? `${MSI_GEOMETRY.radii.button}px` : undefined
    ),
    backgroundColor: (theme) => getPerformanceChartChrome(theme).control,
    boxShadow: (theme) => (usesAppStyleGrammar(theme) ? 'none' : undefined),
};

export const performanceToggleButtonSx = {
    color: (theme) => getPerformanceChartChrome(theme).secondaryText,
    letterSpacing: (theme) => (usesAppStyleGrammar(theme) ? 0 : undefined),
    '&.Mui-selected': {
        borderColor: (theme) => (
            usesAppStyleGrammar(theme)
                ? MSI_COMPONENT_COLORS.focusBorder
                : '#777'
        ),
        backgroundColor: (theme) => (
            usesAppStyleGrammar(theme) ? MSI_PALETTE.blue100 : '#11161c'
        ),
        color: (theme) => getPerformanceChartChrome(theme).text,
    },
    '&.Mui-selected:hover': {
        backgroundColor: (theme) => (
            usesAppStyleGrammar(theme) ? MSI_PALETTE.blue100 : '#1a232d'
        ),
    },
};

export const performanceSecondaryControlSx = {
    borderColor: (theme) => getPerformanceChartChrome(theme).controlBorder,
    borderRadius: (theme) => (
        usesAppStyleGrammar(theme) ? `${MSI_GEOMETRY.radii.button}px` : undefined
    ),
    backgroundColor: (theme) => getPerformanceChartChrome(theme).control,
    color: (theme) => getPerformanceChartChrome(theme).secondaryText,
    letterSpacing: (theme) => (usesAppStyleGrammar(theme) ? 0 : undefined),
    textTransform: 'none',
    boxShadow: (theme) => (usesAppStyleGrammar(theme) ? 'none' : undefined),
    '&:hover': {
        borderColor: (theme) => getPerformanceChartChrome(theme).controlHoverBorder,
        backgroundColor: (theme) => getPerformanceChartChrome(theme).controlHover,
        color: (theme) => getPerformanceChartChrome(theme).text,
    },
};

export const performanceViewTabsSx = {
    minHeight: 36,
    minWidth: 0,
    maxWidth: '100%',
    '& .MuiTabs-flexContainer': { gap: 0.75 },
    '& .MuiTabs-indicator': { display: 'none' },
    '& .MuiTab-root': {
        minHeight: 34,
        minWidth: 0,
        px: 1.25,
        py: 0.75,
        border: `1px solid ${MSI_PALETTE.line}`,
        borderRadius: '6px',
        backgroundImage: 'none',
        boxShadow: 'none',
        backgroundColor: MSI_PALETTE.surface,
        color: MSI_PALETTE.ink700,
        fontSize: 12,
        fontWeight: 600,
        lineHeight: 1.3,
        letterSpacing: 0,
        textTransform: 'none',
        opacity: 1,
        transition: 'background-color 150ms ease, border-color 150ms ease',
        '&:hover': { backgroundColor: MSI_PALETTE.infoBg, borderColor: MSI_COMPONENT_COLORS.focusBorder },
        '&.Mui-selected': {
            backgroundColor: MSI_PALETTE.infoBg,
            borderColor: MSI_COMPONENT_COLORS.focusBorder,
            color: MSI_PALETTE.blue600,
            boxShadow: 'none',
            fontWeight: 700,
        },
        '&.Mui-focusVisible': { outline: `2px solid ${MSI_PALETTE.blue600}`, outlineOffset: -3 },
    },
    '& .MuiTabScrollButton-root': { width: 28, height: 34, borderRadius: '6px' },
};

export const performancePopoverPaperSx = {
    border: (theme) => `1px solid ${getPerformanceChartChrome(theme).tooltipBorder}`,
    borderRadius: (theme) => (
        usesAppStyleGrammar(theme) ? `${MSI_GEOMETRY.radii.panel}px` : undefined
    ),
    backgroundColor: (theme) => getPerformanceChartChrome(theme).tooltip,
    backgroundImage: (theme) => (usesAppStyleGrammar(theme) ? 'none' : undefined),
    color: (theme) => getPerformanceChartChrome(theme).text,
    boxShadow: (theme) => (
        usesAppStyleGrammar(theme) ? '0 8px 24px rgba(12, 23, 38, 0.12)' : undefined
    ),
};

export const performancePickerPopperSx = {
    '& .MuiPaper-root': performancePopoverPaperSx,
    '& .MuiIconButton-root': {
        color: (theme) => getPerformanceChartChrome(theme).secondaryText,
    },
    '& .MuiDayCalendar-weekDayLabel': {
        color: (theme) => getPerformanceChartChrome(theme).mutedText,
    },
    '& .MuiPickersDay-root, & .MuiPickersYear-yearButton, & .MuiPickersMonth-root button': {
        color: (theme) => getPerformanceChartChrome(theme).secondaryText,
    },
    '& .MuiPickersDay-root.Mui-selected': {
        backgroundColor: (theme) => (
            usesAppStyleGrammar(theme) ? MSI_PALETTE.blue600 : '#1976d2'
        ),
        color: (theme) => getAppStyleTokens(theme).actions.primaryText,
    },
};

export function getPerformancePickerTextFieldProps(theme, { highlight = false } = {}) {
    const chrome = getPerformanceChartChrome(theme);
    return {
        size: 'small',
        variant: 'outlined',
        InputProps: {
            sx: {
                height: 32,
                color: chrome.text,
                backgroundColor: highlight
                    ? (usesAppStyleGrammar(theme) ? MSI_PALETTE.warningBg : 'rgba(255, 224, 130, 0.8)')
                    : chrome.control,
                transition: 'background-color 180ms ease',
                '& .MuiIconButton-root': { color: chrome.secondaryText },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: chrome.controlBorder },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: chrome.controlHoverBorder },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: usesAppStyleGrammar(theme)
                        ? MSI_COMPONENT_COLORS.focusBorder
                        : '#69a1ff',
                },
                '& input': {
                    color: chrome.text,
                    WebkitTextFillColor: chrome.text,
                    fontWeight: 600,
                },
            },
        },
    };
}

export function getPerformanceTooltipStyle(theme) {
    const chrome = getPerformanceChartChrome(theme);
    return {
        background: chrome.tooltip,
        border: `1px solid ${chrome.tooltipBorder}`,
        borderRadius: `${MSI_GEOMETRY.radii.iconButton}px`,
        color: chrome.text,
        boxShadow: usesAppStyleGrammar(theme)
            ? '0 8px 24px rgba(12, 23, 38, 0.12)'
            : 'none',
        padding: 8,
    };
}

export const performanceCompactLabelSx = {
    color: (theme) => getPerformanceChartChrome(theme).mutedText,
    fontSize: (theme) => (
        usesAppStyleGrammar(theme) ? MSI_TYPE_SCALE.tableHeader.fontSize : undefined
    ),
    fontWeight: (theme) => (
        usesAppStyleGrammar(theme) ? MSI_TYPE_SCALE.tableHeader.fontWeight : undefined
    ),
    letterSpacing: (theme) => (usesAppStyleGrammar(theme) ? 0 : undefined),
    textTransform: (theme) => (usesAppStyleGrammar(theme) ? 'uppercase' : undefined),
};

export const performanceDragMarkerSx = {
    backgroundColor: (theme) => getPerformanceChartChrome(theme).tooltip,
    color: (theme) => getPerformanceChartChrome(theme).secondaryText,
    borderRadius: (theme) => (
        usesAppStyleGrammar(theme) ? `${MSI_GEOMETRY.radii.iconButton}px` : undefined
    ),
    boxShadow: (theme) => (
        usesAppStyleGrammar(theme) ? '0 6px 16px rgba(12, 23, 38, 0.14)' : undefined
    ),
};
